import classNames from 'classnames';
import React from 'react';
import { Fund } from 'src/components/Exchange/types';
import {
  IRA_CONTRIBUTIONS_MODAL,
  START_DATE_TOOL_TIP,
} from 'src/redux/modules/modals/modals.module';
import {
  TRANSFER_DAYS,
  TRANSFER_MONTHS,
} from 'src/redux/modules/shoppingCart/shoppingCart.module';
import {
  collegeMaxPlanLimits,
  selectableDays,
  selectableMonths,
} from 'src/settings/settings';
import { isAlaska529 } from 'src/utils/funds/college/college-fund-notes-utility';
import { isIRA } from 'src/utils/IRA/IRA-utility';
import {
  isRecurringBuyEdit,
  isRecurringContribute,
} from 'src/utils/transactions-util';
import RetailContributionsEditor from '../../Buy/BuyFlow/RetailContributionsEditor/RetailContributionsEditor';
import If from '../../Shared/If/If';
import { SelectableGrid } from '../../Shared/SelectableGrid';
import './RecurringBuyFundEditor.scss';

export interface RecurringBuyFundEditorProps {
  customerWebViewEnabled: boolean;
  isSalesforceRep: boolean;
  fund: Fund;
  actions: Record<string, CallableFunction>;
  minValue: number;
  maxValue: number;
}
const RecurringBuyFundEditor = ({
  customerWebViewEnabled,
  isSalesforceRep,
  fund,
  actions,
  maxValue,
  minValue,
}) => {
  const { openModal, updateDaySelection, updateMonthSelection } = actions;
  const { agreementID, BPID, productType, transferDays, transferMonths } = fund;

  function showIRAContributions() {
    openModal(IRA_CONTRIBUTIONS_MODAL, {
      showSelectedYearOnlySmallVP: true,
      fund,
    });
  }

  function showStartDateToolTip() {
    openModal(START_DATE_TOOL_TIP);
  }
  const { max } = collegeMaxPlanLimits[productType] || false; // default false
  return (
    <React.Fragment>
      <div className={'rb-fund-editor-container'}>
        <div
          className={classNames('rb-fund-editor-input-container', {
            'rb-fund-editor-edit-container': isRecurringBuyEdit(),
          })}
        >
          <div className="rb-fund-editor-recurring-amount">
            <RetailContributionsEditor
              collegePlanMaxValue={max}
              {...{ fund, actions, maxValue, minValue }}
            />
            <If test={isIRA(fund)}>
              <div className="rb-fund-editor-contribution-info-container">
                <a
                  className="rb-fund-editor-contribution-info-link"
                  onClick={showIRAContributions}
                >
                  <div className="rb-fund-editor-contribution-info-text">
                    {' '}
                    View Contribution Table
                  </div>
                </a>
              </div>
            </If>
          </div>
          <div className="rb-fund-editor-frequency-container">
            <SelectableGrid
              showSelectAllOption
              allItems={selectableMonths}
              buttonProps={{
                btnSize: 64,
                phoneColNum: 3,
                idPrefix(id) {
                  return `monthButton_${id}`;
                },
              }}
              handleSelect={(selected, isAllSelected) =>
                updateMonthSelection(
                  agreementID,
                  selected,
                  TRANSFER_MONTHS,
                  BPID,
                  isAllSelected
                )
              }
              id={`month-chart-${agreementID}`}
              optionName="Months"
              selectedItems={transferMonths}
              title="Months"
            />
          </div>
          <div className="rb-fund-editor-date-container">
            <SelectableGrid
              allItems={selectableDays}
              buttonProps={{
                btnSize: 40,
                phoneColNum: 5,
                idPrefix(id) {
                  return `dayButton_${id}`;
                },
              }}
              handleSelect={(selected) =>
                updateDaySelection(agreementID, selected, TRANSFER_DAYS, BPID)
              }
              id={`day-chart-${agreementID}`}
              limitText="Max: 4 days per month"
              maxSelectCount={4}
              selectedItems={transferDays}
              showSelectAllOption={false}
              title="Date(s)"
            >
              <div className="rb-fund-editor-disclaimer-txt">
                {!customerWebViewEnabled && isSalesforceRep ? (
                  <span>
                    <p>
                      By selecting this service, you acknowledge that automatic{' '}
                      {isRecurringContribute()
                        ? 'contributions'
                        : 'transactions'}{' '}
                      will be placed in accordance with the following:
                    </p>
                    <br />
                    <p>
                      Automatic{' '}
                      {isRecurringContribute()
                        ? 'Contributions'
                        : 'Transactions'}{' '}
                      programs are typically established the following business
                      day, but can take up to three business days to be
                      completed.
                    </p>
                    <br />
                    <p>
                      Except for certain dates as noted below, if one of your
                      chosen dates occurs on a weekend or holiday, the trade
                      will be placed on the next business day at that business
                      day’s Net Asset Value (NAV).
                    </p>
                    <br />
                    <p>
                      <b>Exception:</b> If your chosen date falls either on a
                      day that does not exist on the calendar (ex. February
                      31st) or on the last weekend of the year and the next
                      business day is the following year, your trade will be
                      placed on the previous business day at that business day’s
                      NAV.
                    </p>
                  </span>
                ) : (
                  <span>
                    <a
                      className="rb-fund-editor-link college-branded-link"
                      onClick={showStartDateToolTip}
                    >
                      View important information
                    </a>{' '}
                    regarding scheduled dates of your automatic{' '}
                    {isRecurringContribute() ? 'contributions' : 'transactions'}
                    .
                  </span>
                )}
              </div>
            </SelectableGrid>
          </div>
          <If test={isAlaska529(fund)}>
            <p>
              <br />
              To be considered for the{' '}
              <a
                className="rb-fund-editor-link college-branded-link college-branded-underline"
                href="https://www.alaska529plan.com/account-holder.html"
                rel="noreferrer"
                target="_blank"
              >
                Dash to Save More&trade;
              </a>
               incentive program, your first automatic monthly contribution must
              be received prior to the last calendar day of the qualifying
              year.***
            </p>
          </If>
        </div>
        <If test={isIRA(fund)}>
          <div className="rb-fund-editor-retirement-note-container">
            <div className="rb-fund-editor-retirement-note">
              <span className="rb-fund-editor-title">Note: </span>
              Automatic IRA contributions cannot be attributed to the prior
              year.
            </div>
          </div>
        </If>
      </div>
    </React.Fragment>
  );
};

export default RecurringBuyFundEditor;
